import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, Loader2, Package, Briefcase } from "lucide-react";

/**
 * Search box with live autocomplete suggestions (debounced) backed by
 * GET /api/listings/suggest. Click a suggestion → listing; "See all" → /browse.
 */
export const SearchAutocomplete = ({
  className = "",
  inputClassName = "",
  placeholder = "Search products, services, countries…",
  onNavigate,
}) => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef(null);
  const tRef = useRef(null);

  useEffect(() => {
    if (tRef.current) clearTimeout(tRef.current);
    if (q.trim().length < 2) { setItems([]); return; }
    tRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/listings/suggest", { params: { q: q.trim() } });
        setItems(data.suggestions || []);
        setOpen(true);
      } catch { /* ignore */ } finally { setLoading(false); }
    }, 220);
    return () => tRef.current && clearTimeout(tRef.current);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const finish = () => { setOpen(false); onNavigate?.(); };
  const go = (term) => { finish(); navigate(`/browse?q=${encodeURIComponent(term)}`); };
  const goListing = (it) => { finish(); navigate(`/listing/${it.listing_id}`); };
  const submit = (e) => { e.preventDefault(); if (q.trim()) go(q.trim()); };

  const onKey = (e) => {
    if (!open || !items.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, -1)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); goListing(items[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <form onSubmit={submit} className={`relative ${className}`} ref={boxRef}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
      <Input
        data-testid="nav-search-input"
        value={q}
        onChange={(e) => { setQ(e.target.value); setActive(-1); }}
        onFocus={() => items.length && setOpen(true)}
        onKeyDown={onKey}
        placeholder={placeholder}
        className={`pl-9 w-full ${inputClassName}`}
        autoComplete="off"
      />
      {open && q.trim().length >= 2 && (
        <div
          data-testid="search-suggestions"
          className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border border-black/10 shadow-xl overflow-hidden z-50 max-h-[60vh] overflow-y-auto"
        >
          {loading && items.length === 0 ? (
            <div className="px-4 py-3 text-sm text-muted-foreground flex items-center">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Searching…
            </div>
          ) : items.length ? (
            <>
              {items.map((it, i) => (
                <button
                  type="button"
                  key={it.listing_id}
                  data-testid={`suggestion-${it.listing_id}`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => goListing(it)}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[#F3EFE9] transition-colors ${active === i ? "bg-[#F3EFE9]" : ""}`}
                >
                  <div className="w-9 h-9 rounded bg-[#F3EFE9] overflow-hidden shrink-0 flex items-center justify-center">
                    {it.image ? (
                      <img src={it.image} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                    ) : it.type === "service" ? (
                      <Briefcase className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <Package className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#1A2024] truncate">{it.label}</div>
                    <div className="text-xs text-muted-foreground truncate capitalize">{it.category || it.type}</div>
                  </div>
                </button>
              ))}
              <button
                type="button"
                onClick={() => go(q.trim())}
                data-testid="suggestion-see-all"
                className="w-full text-left px-3 py-2.5 text-sm font-semibold text-[#C05A3C] border-t border-black/5 hover:bg-[#F3EFE9]"
              >
                See all results for “{q.trim()}” →
              </button>
            </>
          ) : (
            <div className="px-4 py-3 text-sm text-muted-foreground">
              No matches.{" "}
              <button type="button" onClick={() => go(q.trim())} className="text-[#C05A3C] font-medium">Search anyway →</button>
            </div>
          )}
        </div>
      )}
    </form>
  );
};

export default SearchAutocomplete;
